<template>
  <div class="scanner-wrapper">
    <v-btn v-if="torch !== null" class="torch-button" @click="torch = !torch">
      <svg
        v-if="torch"
        class="bolt-icon"
        style="margin-left: -0.5em"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 448 512"
      >
        <!--! Font Awesome Pro 6.4.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2023 Fonticons, Inc. -->
        <path
          d="M112 288l163.6 25.2L171.8 231.4 112 288zm321.3 25.7L528 224 356.7 197.6 416 0 241.8 165 48.4 14.8 29.4 .1 0 38 19 52.7 591.5 497.2l19 14.7L639.9 474l-19-14.7L433.4 313.7zm-151.2 4.6L224 512 362 381.2l-79.9-63z"
        />
      </svg>
      <svg
        v-else
        class="bolt-icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 448 512"
      >
        <!--! Font Awesome Pro 6.4.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2023 Fonticons, Inc. -->
        <path d="M0 288L304 0 244.7 197.6 416 224 112 512l59.3-197.6L0 288z" />
      </svg>
    </v-btn>
    <div v-if="focusLimits" class="focus-mode">
      {{ focusMode }}
    </div>
    <v-slider
      v-show="focusLimits && focusMode === 'M'"
      v-model="focusDistance"
      v-bind="{ ...focusLimits }"
      color="white"
      class="focus-slider"
      direction="vertical"
    />
    <div :id="wrapperId" @click="changeFocusMode"></div>
    <div class="bottom-bar">
      <v-slider
        v-if="advanced && zoomLimits"
        v-model="zoomLevel"
        v-bind="{ ...zoomLimits }"
        color="white"
        class="zoom-slider"
      />
      <div class="fallback-scan">
        <span class="fallback-label">Scanner not working?</span>
        <label for="qr-native-capture" class="fallback-btn"
          >Tap here to scan</label
        >
        <input
          id="qr-native-capture"
          type="file"
          accept="image/*"
          capture="environment"
          style="display: none"
          @change="onNativeScanFile"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted, onBeforeUnmount } from "vue";
import {
  Html5Qrcode,
  Html5QrcodeScanType,
  Html5QrcodeScannerState,
  Html5QrcodeSupportedFormats,
} from "html5-qrcode";
import usePinch from "../composables/usePinch";
import useQRScanner from "../composables/useQRScanner";
import DATA_BEEP from "./data-beep";
import DATA_BEEP_TINY from "./data-beep-tiny";

export default {
  name: "QRCodeScanner",
  props: {
    advanced: {
      type: Boolean,
      default: false,
    },
    qrbox: {
      type: Number,
      default: 200,
    },
    fps: {
      type: Number,
      default: 5,
    },
  },
  setup(props, { emit }) {
    const wrapperId = "qr-code-reader";

    const torch = ref(null);
    const focusMode = ref(null);
    const focusDistance = ref(null);
    const focusLimits = ref(null);
    const { zoomLevel, zoomLimits } = usePinch(wrapperId);

    const { tryDecodeDevice, $scannedDevice, $scannedGateway } =
      useQRScanner(false);
    const beepSound = new Audio(DATA_BEEP);
    const beepTinySound = new Audio(DATA_BEEP_TINY);
    /** @type Html5Qrcode|null */
    let html5QrCode = null;
    let _barcodeDetectorTimer = null;

    /** @type import("html5-qrcode/html5-qrcode").Html5QrcodeCameraScanConfig */
    const config = {
      fps: props.fps,
      qrbox: props.qrbox,
      supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA],
      // disableFlip: true,
      videoConstraints: {
        facingMode: "environment",
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      },
    };

    onMounted(() => {
      // console.log("QR scanner mounted");
      html5QrCode = new Html5Qrcode(wrapperId, {
        // verbose: true,
        useBarCodeDetectorIfSupported: false,
        formatsToSupport: [
          Html5QrcodeSupportedFormats.QR_CODE,
          Html5QrcodeSupportedFormats.DATA_MATRIX,
        ],
      });
      startScanning();
    });
    onBeforeUnmount(stopScanning);

    function onScanSuccess(decodedText, { result }) {
      const { text, format } = result;
      const isDataMatrix =
        format.format === Html5QrcodeSupportedFormats.DATA_MATRIX;
      const decodeResult = tryDecodeDevice(decodedText, isDataMatrix);
      if (decodeResult === "partial") {
        beepTinySound.play();
      } else if (decodeResult === "device") {
        pauseScanning();
        beepSound.play();
        emit("detect", { text, format: format.formatName });
        emit("detect-device", $scannedDevice.value);
      } else if (decodeResult === "gateway") {
        pauseScanning();
        beepSound.play();
        emit("detect", { text, format: format.formatName });
        emit("detect-gateway", $scannedGateway.value);
      } else {
        emit("detect", { text, format: format.formatName });
      }
    }

    async function startScanning() {
      // console.log("starting", html5QrCode.getState());
      if (html5QrCode.getState() === Html5QrcodeScannerState.SCANNING) return;
      try {
        await html5QrCode.start({}, config, onScanSuccess, () => {});
        if (props.advanced && getCameraCapability("torch") === true) {
          torch.value = false;
        }

        zoomLimits.value = getCameraCapability("zoom");
        const restoredZoomLevel = parseFloat(
          window.localStorage.getItem("zoom-level")
        );
        console.log("restored", restoredZoomLevel);
        if (zoomLimits.value && restoredZoomLevel) {
          zoomLevel.value = restoredZoomLevel;
        }

        if (
          props.advanced &&
          getCameraCapability("focusMode")?.includes("manual")
        ) {
          focusLimits.value = getCameraCapability("focusDistance");
          if (focusLimits.value?.max > 100) {
            focusLimits.value.max = 100;
          }
          focusDistance.value = focusLimits.value?.min;
        }

        emit("started", true);

        // Start direct BarcodeDetector loop on the video element.
        // html5-qrcode's internal BarcodeDetector integration crops to qrbox
        // before detection, which breaks on Samsung. Calling detect() directly
        // on the video element scans the full frame with no intermediate crop.
        startDirectBarcodeDetector();
      } catch (error) {
        console.error("QRCodeScanner failed to start:", error);
      }
    }

    function startDirectBarcodeDetector() {
      if (!("BarcodeDetector" in window)) return;

      const wrapper = document.getElementById(wrapperId);
      const video = wrapper?.querySelector("video");
      if (!video) return;

      const detector = new window.BarcodeDetector({
        formats: ["qr_code", "data_matrix"],
      });

      async function tick() {
        if (
          ![
            Html5QrcodeScannerState.SCANNING,
            Html5QrcodeScannerState.PAUSED,
          ].includes(html5QrCode.getState())
        )
          return;

        if (html5QrCode.getState() === Html5QrcodeScannerState.SCANNING) {
          try {
            const results = await detector.detect(video);
            if (results.length > 0) {
              const { rawValue, format } = results[0];
              const isDataMatrix = format === "data_matrix";
              const decodeResult = tryDecodeDevice(rawValue, isDataMatrix);
              if (decodeResult === "partial") {
                beepTinySound.play();
              } else if (decodeResult === "device") {
                pauseScanning();
                beepSound.play();
                emit("detect-device", $scannedDevice.value);
                return;
              } else if (decodeResult === "gateway") {
                pauseScanning();
                beepSound.play();
                emit("detect-gateway", $scannedGateway.value);
                return;
              }
            }
          } catch (_) {}
        }

        _barcodeDetectorTimer = setTimeout(tick, 1000 / props.fps);
      }

      _barcodeDetectorTimer = setTimeout(tick, 500);
    }
    function pauseScanning() {
      if (html5QrCode.getState() !== Html5QrcodeScannerState.SCANNING) return;
      html5QrCode.pause(true);
    }
    function resumeScanning() {
      if (html5QrCode.getState() !== Html5QrcodeScannerState.PAUSED) return;
      html5QrCode.resume();
    }
    async function stopScanning() {
      // console.log("stopping");
      clearTimeout(_barcodeDetectorTimer);
      if (
        ![
          Html5QrcodeScannerState.SCANNING,
          Html5QrcodeScannerState.PAUSED,
        ].includes(html5QrCode.getState())
      )
        return;
      try {
        await html5QrCode.stop();
      } catch (error) {
        console.error(error);
      }
    }

    function getCameraCapability(capability) {
      if (
        ![
          Html5QrcodeScannerState.SCANNING,
          Html5QrcodeScannerState.PAUSED,
        ].includes(html5QrCode.getState())
      )
        return null;
      const capabilities = html5QrCode.getRunningTrackCapabilities();
      return capabilities[capability];
    }

    async function applyConstraints(constraints) {
      if (
        !constraints ||
        html5QrCode.getState() !== Html5QrcodeScannerState.SCANNING
      )
        return false;

      // console.log(constraints);
      await html5QrCode.applyVideoConstraints({
        ...constraints,
        advanced: [{ ...constraints }],
      });

      const settings = html5QrCode.getRunningTrackSettings();
      for (const key in constraints) {
        if (settings[key] == constraints[key]) {
          // console.log(`${key} was indeed updated! success`);
        } else {
          console.log(`Failed to set ${key}. Why?`);
          return false;
        }
      }
      return true;
    }

    watch(torch, (newValue, oldValue) => {
      let torchConstrain = undefined;
      if (newValue === true && oldValue === false) {
        torchConstrain = true;
      } else if (newValue === false && oldValue === true) {
        torchConstrain = false;
      }

      if (torchConstrain !== undefined) {
        applyConstraints({ torch: torchConstrain });
      }
    });

    watch(zoomLevel, async (val) => {
      await applyConstraints({ zoom: val });
      window.localStorage.setItem("zoom-level", zoomLevel.value);
    });

    function changeFocusMode() {
      // dump capabilities
      const settings = html5QrCode.getRunningTrackSettings();
      console.log(settings);
      const capabilities = html5QrCode.getRunningTrackCapabilities();
      console.log(capabilities);

      if (focusLimits.value) {
        focusMode.value = focusMode.value === "A" ? "M" : "A";
        if (focusMode.value === "A") {
          applyConstraints({ focusMode: "continuous" });
        } else {
          applyConstraints({
            focusMode: "manual",
            focusDistance: focusDistance.value,
          });
        }
      }
    }
    watch(focusDistance, (val) => applyConstraints({ focusDistance: val }));

    async function onNativeScanFile(event) {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;
      try {
        let decodedText = null;
        let isDataMatrix = false;
        if ("BarcodeDetector" in window) {
          const detector = new window.BarcodeDetector({
            formats: ["qr_code", "data_matrix"],
          });
          const bitmap = await createImageBitmap(file);
          const results = await detector.detect(bitmap);
          if (results.length > 0) {
            decodedText = results[0].rawValue;
            isDataMatrix = results[0].format === "data_matrix";
          }
        } else {
          decodedText = await html5QrCode.scanFile(file, false);
        }
        if (!decodedText) return;
        const decodeResult = tryDecodeDevice(decodedText, isDataMatrix);
        if (decodeResult === "partial") {
          beepTinySound.play();
        } else if (decodeResult === "device") {
          pauseScanning();
          beepSound.play();
          emit("detect-device", $scannedDevice.value);
        } else if (decodeResult === "gateway") {
          pauseScanning();
          beepSound.play();
          emit("detect-gateway", $scannedGateway.value);
        } else {
          emit("detect", {
            text: decodedText,
            format: isDataMatrix ? "DATA_MATRIX" : "QR_CODE",
          });
        }
      } catch (e) {
        console.error("Native scan failed:", e);
      }
    }

    return {
      wrapperId,
      startScanning,
      stopScanning,
      pauseScanning,
      resumeScanning,
      getCameraCapability,
      torch,
      focusMode,
      focusDistance,
      focusLimits,
      zoomLevel,
      zoomLimits,
      changeFocusMode,
      onNativeScanFile,
    };
  },
};
</script>

<style scoped>
.scanner-wrapper {
  position: relative;
}
.torch-button {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  padding: 0;
  margin: 0;
  min-width: 5em;
  min-height: 4em;
  border: none;
  background-color: transparent;
  box-shadow: none;
}
.bolt-icon {
  fill: white;
  height: 1.3em;
  width: 2em;
}
.focus-mode {
  position: absolute;
  top: 3px;
  right: 0.4em;
  z-index: 10;
  color: lightgrey;
  font-weight: bold;
}
.focus-slider {
  position: absolute;
  top: 1em;
  right: 0.5em;
  z-index: 10;
}
.bottom-bar {
  display: flex;
  flex-direction: column;
  background: rgba(0, 0, 0, 0.6);
  padding: 4px 12px 8px;
}
.zoom-slider {
  width: calc(100% - 1em);
  padding: 0 2em;
}
.fallback-scan {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 6px 0 2px;
}
.fallback-label {
  color: #ccc;
  font-size: 13px;
}
.fallback-btn {
  color: #fff;
  background: #1976d2;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
}
</style>
